import { z } from 'zod';

export const PROJECT_TYPES = ['scrum', 'kanban'] as const;
export const projectTypeEnum = z.enum(PROJECT_TYPES);
export type ProjectType = z.infer<typeof projectTypeEnum>;

export const PROJECT_STATUSES = ['planning', 'active', 'on_hold', 'completed', 'archived'] as const;
export const projectStatusEnum = z.enum(PROJECT_STATUSES);
export type ProjectStatus = z.infer<typeof projectStatusEnum>;

/** Per-project role, independent of the caller's HR role — see `projectMembership.ts`. */
export const PROJECT_MEMBER_ROLES = ['owner', 'admin', 'member', 'viewer'] as const;
export const projectMemberRoleEnum = z.enum(PROJECT_MEMBER_ROLES);
export type ProjectMemberRole = z.infer<typeof projectMemberRoleEnum>;

/**
 * A Jira/Trello-style work tracker — a brand-new domain for VEGAAHI with
 * its own membership-based authorization rather than the HR role hierarchy
 * every other module uses (see the Project Management plan's RBAC
 * decision). Ported from Office_project's `Project` model.
 */
export const createProjectSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120),
  /** Short uppercase code (e.g. "EMS") — auto-generated from the name server-side when omitted. */
  key: z.string().trim().toUpperCase().max(8).optional().or(z.literal('')),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  type: projectTypeEnum.default('scrum'),
  color: z.string().trim().max(20).default('#1E40AF'),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
  memberIds: z.array(z.string()).default([]),
});

export type CreateProjectInput = z.infer<typeof createProjectSchema>;

/** `key`/`type` are immutable after creation — mirrors the source's own field allow-list on update. */
export const updateProjectSchema = z.object({
  name: z.string().trim().min(2, 'Name must be at least 2 characters').max(120).optional(),
  description: z.string().trim().max(2000).optional().or(z.literal('')),
  status: projectStatusEnum.optional(),
  color: z.string().trim().max(20).optional(),
  startDate: z.coerce.date().optional(),
  endDate: z.coerce.date().optional(),
});

export type UpdateProjectInput = z.infer<typeof updateProjectSchema>;

export const addProjectMemberSchema = z.object({
  userId: z.string().min(1, 'userId is required'),
  role: projectMemberRoleEnum.default('member'),
});

export type AddProjectMemberInput = z.infer<typeof addProjectMemberSchema>;
